/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { PostSigninInteractor } from "../../model/interactor";
import { SmartSearchScreenInteractor } from "../../model/interactor/shared";
import { Log } from "../../model/logging";
import type { TCarouselItem } from "../../model/types";
import { SEE_MORE_RAIL_TILE_CAP } from "../utility/carouselPagination";
import { getImageHostBaseUrl, mapSmartSearchCarouselToRail } from "../utility";
import { fetchCarouselTilePage } from "../utility/smartCarouselUtility";
import { fetchShowflixCarouselTilePage } from "../utility/showflixCarouselUtility";
import { fetchMyHubCarouselTilePage } from "../myHub";
import type { UnityAppDispatch, UnityRootState } from "../UnityAppStore";
import {
  appendSeeMoreItems,
  appendSeeMoreCursorPage,
  setSeeMoreLoadingMore,
  selectSeeMoreHasMore,
  selectSeeMoreIsLoadingMore,
  selectSeeMoreItems,
  selectSeeMorePageCursor,
  selectSeeMoreSource
} from "./seeMoreSlice";
import { SeeMoreScreen } from "./seeMore.types";

const TAG: string = "seeMoreThunks";

/**
 * Fetches one tile page for the carousel the See More grid is paging, routed
 * to whichever backend that source screen's rail actually lives behind.
 *
 * Deliberately an explicit check per screen rather than a catch-all default:
 * a My Hub carousel ID means nothing to the smart-carousel endpoint (and vice
 * versa), so a screen this switch does not recognize must fail loudly rather
 * than silently page the wrong service with the wrong ID.
 *
 * @throws if `source.screen` is not one of the known screens
 */
type TSeeMorePage = {
  items: TCarouselItem[];
  pageCursor?: string | null;
};

const fetchSeeMorePage = (
  screen: SeeMoreScreen,
  carouselId: string,
  offset: number,
  count: number,
  showflixGenres: string[] | undefined,
  searchTerm: string | undefined,
  pageCursor: string | null
): Promise<TSeeMorePage> => {
  switch (screen) {
    case SeeMoreScreen.HOME_SCREEN:
      return fetchCarouselTilePage(carouselId, offset, count);
    case SeeMoreScreen.MY_HUB:
      return fetchMyHubCarouselTilePage(carouselId, offset, count);
    case SeeMoreScreen.SHOWS_AND_MOVIES:
      return fetchShowflixCarouselTilePage(
        carouselId,
        offset,
        count,
        showflixGenres || []
      );
    case SeeMoreScreen.SEARCH_SCREEN:
      if (searchTerm === undefined || searchTerm.length === 0) {
        throw new Error(
          "fetchSeeMorePage() - Search source has no normalized search term"
        );
      }
      if (pageCursor === null || pageCursor.length === 0) {
        throw new Error(
          "fetchSeeMorePage() - Search source has no page cursor"
        );
      }
      return fetchSmartSearchCarouselPage(
        carouselId,
        searchTerm,
        pageCursor,
        count
      );
    default:
      throw new Error(
        `fetchSeeMorePage() — unrecognized source.screen "${String(screen)}"`
      );
  }
};

const fetchSmartSearchCarouselPage = async (
  carouselName: string,
  searchTerm: string,
  pageCursor: string,
  count: number
): Promise<TSeeMorePage> => {
  const interactor: SmartSearchScreenInteractor =
    new SmartSearchScreenInteractor();
  const imageHostBaseUrl: string = await getImageHostBaseUrl(
    new PostSigninInteractor()
  );
  const carousel = await interactor.fetchSmartSearchCarousel({
    carouselName,
    searchTerm,
    pageCursor,
    count
  });
  const rail = mapSmartSearchCarouselToRail(carousel, imageHostBaseUrl);
  return {
    items: rail.items,
    pageCursor: rail.pageCursor === undefined ? null : rail.pageCursor
  };
};

/**
 * Pages the See More grid forward by one tile page.
 *
 * This is the "see ALL the items" half of See More: unlike the source home
 * rail — which caps its in-line tiles at SEE_MORE_RAIL_TILE_CAP so the See More
 * tile has a fixed slot — the grid is deliberately UNCAPPED. It starts from the
 * seeded items handed over by the rail (offset = items.length) and requests a
 * full CAROUSEL_TILE_PAGE_SIZE page each time, appending until the collection
 * is exhausted (items.length >= totalCount).
 *
 * The carousel to page comes from the See More `source`, which the opener
 * (home screen, My Hub, Shows & Movies, …) set on the request — routed to the
 * right backend per screen by {@link fetchSeeMorePage}. Each screen's rail can
 * live behind a different service, so the carousel ID alone is not enough.
 *
 * De-duplicated against isLoadingMore + hasMore so repeat onNearEnd fires while
 * a page is in flight, or after the last page, are no-ops.
 */
export const loadMoreSeeMoreItems =
  () =>
  async (
    dispatch: UnityAppDispatch,
    getState: () => UnityRootState
  ): Promise<void> => {
    const state = getState();

    // Nothing more to fetch, or a page is already in flight.
    if (!selectSeeMoreHasMore(state) || selectSeeMoreIsLoadingMore(state)) {
      return;
    }

    const source = selectSeeMoreSource(state);
    if (source === null || source.carouselId === undefined) {
      Log.w(TAG, "loadMoreSeeMoreItems() — no source carouselId, cannot page");
      return;
    }
    const carouselId: string = source.carouselId;

    // Offset is the flat length of what is already laid out (the seed + any
    // pages appended so far). Count is a FULL page — no cap here.
    const offset: number = selectSeeMoreItems(state).length;
    const count: number = SEE_MORE_RAIL_TILE_CAP;
    const pageCursor: string | null = selectSeeMorePageCursor(state);

    Log.i(
      TAG,
      "loadMoreSeeMoreItems() - screen=" +
        source.screen +
        ", carouselId=" +
        carouselId +
        ", offset=" +
        String(offset) +
        ", count=" +
        String(count)
    );

    dispatch(setSeeMoreLoadingMore(true));
    try {
      const page: TSeeMorePage = await fetchSeeMorePage(
        source.screen,
        carouselId,
        offset,
        count,
        source.showflixGenres,
        source.searchTerm,
        pageCursor
      );
      // appendSeeMoreItems also clears isLoadingMore (and no-ops on an empty
      // page), so success needs no extra setSeeMoreLoadingMore(false).
      if (source.screen === SeeMoreScreen.SEARCH_SCREEN) {
        dispatch(
          appendSeeMoreCursorPage({
            items: page.items,
            pageCursor: page.pageCursor === undefined ? null : page.pageCursor
          })
        );
      } else {
        dispatch(appendSeeMoreItems(page.items));
      }
    } catch (error: unknown) {
      Log.e(TAG, "loadMoreSeeMoreItems() - fetch failed", error);
      dispatch(setSeeMoreLoadingMore(false));
    }
  };
